// Upsell modal triggered when a 402 plan_limit_reached error fires.
// Listens on a window event "vt:plan-limit" so axios interceptors / pages can fire it generically.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, Sparkles } from "lucide-react";

export function PlanLimitUpsell() {
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);

    useEffect(() => {
        const onLimit = (ev) => {
            setInfo(ev.detail || { message: "You've reached your plan's limit.", current: 25, limit: 25 });
        };
        window.addEventListener("vt:plan-limit", onLimit);
        return () => window.removeEventListener("vt:plan-limit", onLimit);
    }, []);

    if (!info) return null;
    const isFeature = info.code === "plan_feature_required";
    const recommended = isFeature && info.feature === "legal_vault" ? "legacy" : "family";
    const recHeader = recommended === "legacy" ? "Legacy — ₹2999/year" : "Family — ₹999/year";
    const recBullets = recommended === "legacy"
        ? ["• Everything in Family", "• Legal Vault", "• 100 GB storage", "• Annual printed family tree", "• Priority support"]
        : ["• Unlimited family members", "• 10 GB media storage", "• AI biography generator", "• All tree styles"];
    return (
        <Dialog open={!!info} onOpenChange={() => setInfo(null)}>
            <DialogContent data-testid="plan-limit-upsell" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#1B5E20]">
                        <Crown className="h-5 w-5 text-[#F57C00]" />
                        {isFeature
                            ? `${info.feature_name || "This feature"} requires an upgrade`
                            : "Free plan limit reached"}
                    </DialogTitle>
                    <DialogDescription className="text-[#444] text-[14px]">
                        {info.message ||
                            (isFeature
                                ? "Upgrade your plan to unlock this feature."
                                : `You've added ${info.current} of ${info.limit} members on the Free plan.`)}
                    </DialogDescription>
                </DialogHeader>
                <div className="rounded-xl border-2 border-[#1B5E20] bg-[#E8F5E9] p-4 mt-2">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[#1B5E20]" />
                        <span className="text-[12px] uppercase tracking-wide font-semibold text-[#1B5E20]">Recommended</span>
                    </div>
                    <div className="mt-1 text-[18px] font-bold text-[#1F1F1F]">{recHeader}</div>
                    <ul className="mt-2 text-[13.5px] text-[#1F1F1F] space-y-0.5">
                        {recBullets.map((b) => <li key={b}>{b}</li>)}
                    </ul>
                </div>
                <div className="mt-4 flex gap-2">
                    <Button
                        data-testid="plan-limit-upgrade"
                        onClick={() => { setInfo(null); navigate("/plans"); }}
                        className="flex-1 h-11 bg-[#1B5E20] hover:bg-[#154d1a] text-white"
                    >
                        See plans
                    </Button>
                    <Button
                        variant="outline"
                        data-testid="plan-limit-dismiss"
                        onClick={() => setInfo(null)}
                        className="h-11"
                    >
                        Maybe later
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default PlanLimitUpsell;
